import React from 'react';
import { Moon, Sun } from 'lucide-react';
import { LanguageSelector } from './LanguageSelector';
import { AuthButton } from './AuthButton';
import { ThemeSelector } from './ThemeSelector';
import { getTranslation } from '../utils/translations';
import { getTextColor } from '../utils/themeUtils';
import type { Language, Theme, UserLocation } from '../types';

interface HeaderProps {
  currentTime: Date;
  isDark: boolean;
  setIsDark: (dark: boolean) => void;
  language: Language;
  onLanguageChange: (language: Language) => void;
  theme: Theme;
  onThemeChange: (theme: Theme) => void;
  userLocation?: UserLocation;
}

export function Header({ 
  currentTime, 
  isDark, 
  setIsDark, 
  language, 
  onLanguageChange,
  theme,
  onThemeChange,
  userLocation
}: HeaderProps) {
  const formatLocationAndDate = () => {
    const locationText = userLocation 
      ? `${userLocation.city || 'Unknown'}, ${userLocation.country || 'Unknown'}`
      : '';
    
    const dateText = currentTime.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const timeText = currentTime.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });

    if (locationText) {
      return `${locationText}, ${dateText}, ${timeText}`;
    }
    return `${dateText}, ${timeText}`;
  };

  return (
    <header className={`sticky top-0 z-40 pt-safe py-3 sm:py-4 px-4 sm:px-6 ${isDark ? 'bg-gray-800/95' : 'bg-white/95'} shadow-md backdrop-blur supports-[backdrop-filter]:bg-opacity-90`}>
      <div className="container mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-3 sm:gap-0">
          {/* Left side - Title and Location/Date/Time */}
          <div className="flex flex-col items-center sm:items-start w-full sm:w-auto">
            <h1 className="text-xl sm:text-2xl font-bold mb-1" style={getTextColor(theme)}>
              {getTranslation('app.title', language)}
            </h1>
            <div className="text-sm sm:text-base font-mono text-center sm:text-left">
              {formatLocationAndDate()}
            </div>
          </div>
          
          {/* Right side - Controls */}
          <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-center sm:justify-end">
            <div className="flex items-center gap-2">
              <LanguageSelector
                currentLanguage={language}
                onLanguageChange={onLanguageChange}
                isDark={isDark}
                theme={theme}
              />
              <ThemeSelector
                currentTheme={theme}
                onThemeChange={onThemeChange}
                isDark={isDark}
              />
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg ${
                  isDark ? 'bg-gray-700 text-yellow-400' : 'bg-gray-100 text-gray-600'
                } hover:opacity-80 transition-opacity`}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
            <div className="hidden sm:block">
              <AuthButton isDark={isDark} language={language} />
            </div>
          </div>
        </div>
        
        {/* Mobile auth button */}
        <div className="sm:hidden mb-2 flex justify-center">
          <AuthButton isDark={isDark} language={language} />
        </div>
      </div>
    </header>
  );
}